import { PriceType } from '@vfde-sails/glados-v2';
import { ADDITIONAL_PAGE_OPTIONS } from '@vfde-sails/constants';
import { StateProps } from 'Container/App/interface';
import { Price } from '@vfde-brix/ws10/core';
import { IOfferSummaryCardProperties } from '@vfde-brix/ws10/offer-summary-card';
import { IButtonProperties } from '@vfde-brix/ws10/button';
import { IPromoPriceProperties } from '@vfde-brix/ws10/promo-price';
import { goToBasket } from 'Container/App/slice';
import { useDispatch } from '../../../store';

/**
 * Get price condition
 */
export const getPriceCondition = (
    isHardwareOnly: boolean | undefined,
    paymentMethod: PriceType | undefined,
): string => {
    if (isHardwareOnly === true || paymentMethod !== undefined) {
        return '';
    }

    return window[ADDITIONAL_PAGE_OPTIONS].offerSummaryCard.priceCondition;
};

/**
 * Monthly price should be shown if insurance is set
 */
export const shouldHideMonthlyPrice = (paymentMethod: PriceType | undefined, isHardwareOnly: boolean | undefined): boolean =>
    !!isHardwareOnly || paymentMethod === undefined;

/**
 * Get monthly price based on config
 */
export const getMonthlyPrice = (state: StateProps): Partial<IPromoPriceProperties> => ({
    strikePrice: state.strikePrice ? Price.fromNumber(state.strikePrice.gross) : undefined,
    ...state.tariffPriceAtRuntimeStart && {
        price: Price.fromNumber(state.tariffPriceAtRuntimeStart.gross),
        optPriceConditionBefore: undefined,
    },
});

/**
 * Compose legal text and replace values with API information
 */
export const composeLegalText = (state: StateProps | null): string[] => {
    const { offerSummaryCard: { legalText, noConnectionFeeLabel } } = window[ADDITIONAL_PAGE_OPTIONS];
    const legalTextTemplate: string[] = [];

    // We only have legal text when the user selected either onenumber or smart tech one number as payment method
    if (state?.customerConfig.paymentMethod && !state?.customerConfig.isHardwareOnly) {
        for (const [index, legalTextItem] of legalText[state.customerConfig.paymentMethod].entries()) {
            if (index === 0 && !state.connectionFee) {
                legalTextTemplate.push(`${noConnectionFeeLabel} ${legalTextItem}`);
            }
            else {
                legalTextTemplate.push(legalTextItem);
            }
        }
    }

    return legalTextTemplate;
};

/**
 * Get subline text
 */
export const getSubline = (state: StateProps, originalProperties: IOfferSummaryCardProperties): string[] | null => {
    if (state.customerConfig.isHardwareOnly) {
        return [window[ADDITIONAL_PAGE_OPTIONS].offerSummaryCard.tariffSubline.hardwareOnly];
    }

    if (state.customerConfig.paymentMethod === PriceType.Onetime) {
        return [window[ADDITIONAL_PAGE_OPTIONS].offerSummaryCard.tariffSubline.onetime];
    }

    if (state.customerConfig.paymentMethod === PriceType.Monthly && state.subscription) {
        return [state.subscription.tariffName];
    }

    return originalProperties.txtSubline;
};

/**
 * Get button text and optionally the link
 */
export const getPrimaryButtonContent = (
    state: StateProps,
    originalProperties: NonNullable<IOfferSummaryCardProperties['containerButtons']>,
): IButtonProperties => {
    const dispatch = useDispatch();

    if (state.customerConfig.isHardwareOnly) {
        return {
            ...originalProperties[0],
            stdContent: window[ADDITIONAL_PAGE_OPTIONS].offerSummaryCard.buttonText.hardwareOnly,
            business: { onClick: () => dispatch(goToBasket()) },
            optDisabled: false,
        };
    }

    if (state.customerConfig.paymentMethod === PriceType.Onetime) {
        return {
            ...originalProperties[0],
            stdContent: window[ADDITIONAL_PAGE_OPTIONS].offerSummaryCard.buttonText.onetime,
            business: { onClick: () => dispatch(goToBasket()) },
            optDisabled: false,
        };
    }

    return {
        ...originalProperties[0],
        optDisabled: state.customerConfig.paymentMethod === undefined,
        business: { onClick: () => dispatch(goToBasket()) },
    };
};
